import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, CheckCircle2 } from "lucide-react";

const SUBJECT_OPTIONS = [
    { label: "Bug", value: "bug" },
    { label: "Demande d'info", value: "demande_info" },
    { label: "Autre", value: "autre" },
];

function SubjectTag({ active, children, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={[
                "inline-flex items-center rounded-full border px-3 py-1.5 text-sm font-medium transition",
                active
                    ? "border-indigo-600 bg-indigo-600 text-white"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
            ].join(" ")}
        >
            {children}
        </button>
    );
}

export default function NotaireSupportWidget({
    userEmail = "",
    userName = "",
    webhookUrl = "https://hooks.zapier.com/hooks/catch/7206204/uulww8g/",
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [subject, setSubject] = useState("autre");
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");

    const email = useMemo(() => (userEmail || "").trim(), [userEmail]);

    const resetForm = () => {
        setSubject("autre");
        setMessage("");
        setSubmitting(false);
        setSuccess(false);
        setError("");
    };

    const handleClose = () => {
        setIsOpen(false);
        setError("");
        setSuccess(false);
    };

    const handleOpen = () => {
        setIsOpen(true);
        setError("");
        setSuccess(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!email) {
            setError("Impossible de préremplir l'email du notaire.");
            return;
        }

        if (!message.trim() || message.trim().length < 10) {
            setError("Merci de renseigner un message d'au moins 10 caractères.");
            return;
        }

        setSubmitting(true);

        try {
            const subjectLabel =
                SUBJECT_OPTIONS.find((opt) => opt.value === subject)?.label || "Autre";

            const payload = {
                titre: `Nouveau message support notaire - ${subjectLabel}`,
                message: `
Objet : ${subjectLabel}
Email notaire : ${email}
Nom : ${userName || "non renseigné"}
Page : ${window.location.href}
Date : ${new Date().toLocaleString("fr-FR")}

Message :
${message.trim()}
      `.trim(),
            };

            const body = new URLSearchParams();
            Object.entries(payload).forEach(([key, value]) => {
                body.append(key, value ?? "");
            });

            const res = await fetch(webhookUrl, {
                method: "POST",
                body,
            });

            if (!res.ok) {
                throw new Error("Webhook non joignable");
            }

            setSuccess(true);
            setMessage("");
            setSubject("autre");
        } catch (err) {
            setError("Une erreur est survenue lors de l'envoi. Merci de réessayer.");
        } finally {
            setSubmitting(false);
        }
    };

    console.log("user email", userEmail)

    return (
        <>
            <button
                type="button"
                aria-label="Ouvrir le support"
                onClick={handleOpen}
                className="fixed bottom-6 right-6 z-50 flex h-16 w-16 items-center justify-center rounded-full bg-black text-white shadow-2xl transition hover:scale-[1.03]"
            >
                <MessageCircle className="h-7 w-7" />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <>
                        <motion.button
                            type="button"
                            aria-label="Fermer le support"
                            onClick={handleClose}
                            className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[2px]"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        />

                        <motion.aside
                            className="fixed bottom-24 right-6 z-50 flex h-[min(720px,calc(100vh-8rem))] w-[min(430px,calc(100vw-2rem))] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl"
                            initial={{ opacity: 0, y: 24, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 18, scale: 0.98 }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className="relative overflow-hidden bg-gradient-to-br from-indigo-500  to-rose-200 p-4 text-white">
                                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.16),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.20),transparent_30%)]" />

                                <div className="relative flex items-start justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-black/80 text-white shadow-lg">
                                            <MessageCircle className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <p className="mb-0 text-sm font-medium text-white/80">
                                                Support Clotere
                                            </p>
                                            <h2 className="mt-1 text-2xl font-bold leading-tight tracking-[-0.03em] text-white">
                                                Besoin d'aide ?
                                            </h2>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        aria-label="Fermer"
                                        onClick={handleClose}
                                        className="rounded-full p-2 text-white/90 transition hover:bg-white/15"
                                    >
                                        <X className="h-6 w-6" />
                                    </button>
                                </div>

                                <p className="relative max-w-md text-sm leading-6 text-white/85">
                                    Envoyez-nous un message, notre équipe reviendra vers vous rapidement.
                                </p>
                            </div>

                            <div className="min-h-0 flex-1 overflow-y-auto bg-zinc-50 px-5 py-5">
                                {success ? (
                                    <div className="">
                                        <div className="flex items-start gap-3">
                                            <CheckCircle2 className="mt-0.5 h-6 w-6 text-green-600" />
                                            <div>
                                                <h3 className="text-base font-semibold text-green-800">
                                                    Message envoyé
                                                </h3>
                                                <p className="mt-1 text-sm text-green-700">
                                                    Votre demande a bien été transmise à notre équipe.
                                                </p>

                                                <div className="mt-4 flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setSuccess(false);
                                                            setError("");
                                                        }}
                                                        className="rounded-2xl border border-green-200 bg-white px-4 py-2 text-sm font-semibold text-green-800 transition hover:bg-green-100"
                                                    >
                                                        Envoyer un autre message
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={handleClose}
                                                        className="rounded-2xl bg-black px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
                                                    >
                                                        Fermer
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <form className="space-y-4" onSubmit={handleSubmit}>
                                        <div>
                                            <label className="mb-2 block text-sm font-medium text-gray-900">
                                                Objet
                                            </label>
                                            <div className="flex flex-wrap gap-2">
                                                {SUBJECT_OPTIONS.map((option) => (
                                                    <SubjectTag
                                                        key={option.value}
                                                        active={subject === option.value}
                                                        onClick={() => setSubject(option.value)}
                                                    >
                                                        {option.label}
                                                    </SubjectTag>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="support-email"
                                                className="mb-1 block text-sm font-medium text-gray-700"
                                            >
                                                Email
                                            </label>
                                            <input
                                                id="support-email"
                                                type="email"
                                                value={email}
                                                disabled
                                                className="w-full rounded-2xl border border-black/10 bg-gray-100 px-4 py-3 text-sm text-gray-500 outline-none"
                                            />
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="support-message"
                                                className="mb-1 block text-sm font-medium text-gray-700"
                                            >
                                                Message
                                            </label>
                                            <textarea
                                                id="support-message"
                                                rows={6}
                                                value={message}
                                                onChange={(e) => setMessage(e.target.value)}
                                                placeholder="Décrivez votre besoin, votre bug ou votre question…"
                                                className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm outline-none placeholder:text-zinc-400 focus:border-black/30"
                                                required
                                            />
                                        </div>

                                        {error && <p className="text-sm text-red-600">{error}</p>}

                                        <div className="flex items-center justify-end gap-3 pt-1">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    resetForm();
                                                    handleClose();
                                                }}
                                                className="rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50"
                                            >
                                                Annuler
                                            </button>

                                            <button
                                                type="submit"
                                                disabled={submitting}
                                                className="inline-flex items-center gap-2 rounded-2xl bg-black px-4 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                                            >
                                                <Send className="h-4 w-4" />
                                                {submitting ? "Envoi en cours…" : "Envoyer"}
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}